/**
 * buildActData.ts — pure functions for building completion acts (protokół odbioru, DOC-02).
 *
 * ARCHITECTURE:
 * - Pure functions only — no React, no I/O, no side effects.
 * - validateActClosures: over-closure guard (T-07-06 threat mitigation).
 *   Runs BEFORE any persistence or PDF generation. Financial safety invariant.
 * - buildActData: builds a CompletionAct + updated ActState.
 *   Act line value = roundTo(closedQty × priceLabor, 2) — SINGLE rounding path (CLAUDE.md rule 8).
 *   Totals: actNetTotal = sum of already-rounded lineValues (no second round on sum).
 *           actVatAmount = roundTo(actNetTotal × vatRate, 2)
 *           actGrossTotal = roundTo(actNetTotal + actVatAmount, 2)
 *
 * THREAT MODEL:
 * - T-07-06: over-closure → validateActClosures rejects BEFORE persistence / PDF
 * - T-07-07: total divergence → single roundTo parity path, no re-round in PDF/JSX
 * - T-07-08: backward-compat → ActState.optional() on ProjectSnapshot; undefined → {acts:[],closedByItem:{}}
 */

import { roundTo } from '@/shared/lib/number';
import { generateId } from '@/shared/lib/id';
import type { ActState, CompletionAct } from '@/shared/validation/schemas';
import type { LineResult } from '@/entities/estimate/lib/computeTotals';

// ---------------------------------------------------------------------------
// validateActClosures — over-closure guard (T-07-06)
// ---------------------------------------------------------------------------

export interface ActClosureInput {
  itemId: string;
  closedQty: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: Array<{ itemId: string; message: string }>;
}

/**
 * Validates that no closure exceeds the remaining quantity for any estimate item.
 *
 * remaining = item.finalQty − (existingActState.closedByItem[itemId] ?? 0)
 *
 * Error codes:
 *   'item_not_found'     — itemId not found in estimateLines
 *   'exceeds_remaining'  — closedQty > remaining (beyond Number.EPSILON tolerance)
 *
 * MUST run before persistence and before PDF generation (PATTERNS constraint 6).
 */
export function validateActClosures(
  closures: ActClosureInput[],
  existingActState: ActState,
  estimateLines: LineResult[],
): ValidationResult {
  const errors: Array<{ itemId: string; message: string }> = [];
  const lineByItem = new Map(estimateLines.map((l) => [l.itemId, l]));

  for (const closure of closures) {
    const line = lineByItem.get(closure.itemId);
    if (!line) {
      errors.push({ itemId: closure.itemId, message: 'item_not_found' });
      continue;
    }
    // CR-02 fix: reject zero/negative qty — a zero-value row in a signed protokół is a
    // data-integrity defect (produces a malformed legal document). CLAUDE.md rules 11/12.
    if (closure.closedQty <= 0) {
      errors.push({ itemId: closure.itemId, message: 'non_positive_qty' });
      continue;
    }
    const alreadyClosed = existingActState.closedByItem[closure.itemId] ?? 0;
    const remaining = line.finalQty - alreadyClosed;
    if (closure.closedQty > remaining + Number.EPSILON) {
      errors.push({
        itemId: closure.itemId,
        message: `exceeds_remaining: ${closure.closedQty} > ${remaining}`,
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// buildActData — builds CompletionAct + updated ActState
// ---------------------------------------------------------------------------

export interface BuildActDataParams {
  existingActState: ActState;
  closures: ActClosureInput[];
  estimateLines: LineResult[];
  vatRate: number;
  actNumber: string;
  actDate: string;
  actType: 'partial' | 'final';
  contractNumber: string;
  qualityAssessment?: string;
  defectsList?: string;
  defectDeadline?: string;
  remarks?: string;
}

export interface BuildActDataResult {
  act: CompletionAct;
  updatedActState: ActState;
}

/**
 * Builds a CompletionAct and the updated ActState after applying the closures.
 *
 * Throws if validateActClosures returns invalid — never produces an over-closing act.
 *
 * Line value = roundTo(closedQty × priceLabor, 2) — parity with computeTotals line 131.
 * actNetTotal = sum of already-rounded lineValues (PL kosztorys: round-line-then-sum).
 * actVatAmount = roundTo(actNetTotal × vatRate, 2)
 * actGrossTotal = roundTo(actNetTotal + actVatAmount, 2)
 */
export function buildActData(params: BuildActDataParams): BuildActDataResult {
  const {
    existingActState,
    closures,
    estimateLines,
    vatRate,
    actNumber,
    actDate,
    actType,
    contractNumber,
    qualityAssessment = '',
    defectsList = '',
    defectDeadline = '',
    remarks = '',
  } = params;

  // --- Guard: reject over-closures BEFORE any mutation (T-07-06) ---
  const validation = validateActClosures(closures, existingActState, estimateLines);
  if (!validation.valid) {
    const errSummary = validation.errors.map((e) => `${e.itemId}: ${e.message}`).join('; ');
    throw new Error(`buildActData: invalid closures — ${errSummary}`);
  }

  const lineByItem = new Map(estimateLines.map((l) => [l.itemId, l]));

  // Build closure records with parity line values
  const actClosures = closures.map((closure) => {
    const line = lineByItem.get(closure.itemId)!; // safe: guard passed
    // SINGLE rounding path — matches computeTotals.ts line 131 convention
    const lineValue = roundTo(closure.closedQty * line.priceLabor, 2);
    return {
      itemId: closure.itemId,
      closedQty: closure.closedQty,
      lineValue,
    };
  });

  // CR-04 fix: re-apply roundTo after summing already-rounded values to prevent IEEE 754
  // float accumulation (e.g. 100.000000000001). CLAUDE.md rule 8: roundTo for all fin sums.
  const actNetTotal = roundTo(actClosures.reduce((s, c) => s + c.lineValue, 0), 2);

  const actVatAmount = roundTo(actNetTotal * vatRate, 2);
  const actGrossTotal = roundTo(actNetTotal + actVatAmount, 2);

  const act: CompletionAct = {
    id: generateId(),
    actNumber,
    actDate,
    actType,
    contractNumber,
    closures: actClosures,
    actNetTotal,
    actVatAmount,
    actGrossTotal,
    qualityAssessment: qualityAssessment || undefined,
    defectsList: defectsList || undefined,
    defectDeadline: defectDeadline || undefined,
    remarks: remarks || undefined,
  };

  // Update closedByItem (cumulative sum per item across all acts)
  const updatedClosedByItem = { ...existingActState.closedByItem };
  for (const closure of closures) {
    updatedClosedByItem[closure.itemId] =
      (updatedClosedByItem[closure.itemId] ?? 0) + closure.closedQty;
  }

  const updatedActState: ActState = {
    acts: [...existingActState.acts, act],
    closedByItem: updatedClosedByItem,
  };

  return { act, updatedActState };
}
